import React from 'react';

import { useTranslation } from 'react-i18next';
import { Col, Form, Row } from 'react-bootstrap';
import { Control, FieldErrors, RegisterOptions, useForm, useWatch } from 'react-hook-form';

import { ICreateWorkerFormData } from 'forms/worker';

import { CountryController } from 'components/shared/CountryController/CountryController';
import { StateController } from 'components/shared/StateController/StateController';

interface ICreateWorkerAddressProps {
    setValue: ReturnType<typeof useForm>['setValue'];
    register: (options?: RegisterOptions) => React.Ref<HTMLInputElement>;
    errors: FieldErrors<ICreateWorkerFormData>;
    control: Control;
    disabled?: boolean;
    loadRecaptcha: () => void;
}

export const CreateWorkerAddress = ({register, errors, control, disabled, loadRecaptcha}: ICreateWorkerAddressProps) => {
    const {t} = useTranslation();
    const country = useWatch({
        control,
        name: 'address.country',
        defaultValue: undefined,
    });

    // TODO: add back in address search EPIC-454
    // const onAddressSelect = (addressData: IAddressSearchControlSelection) => {
    //     setValue('address', {
    //         'street': addressData.fullStreetAddress,
    //         'town': addressData.suburb,
    //         'state': addressData.state,
    //         'postCode': addressData.postCode,
    //         'country': 'AU',
    //     });
    // };

    return (
        <div className="CreateWorker__form-section">
            <div className="form__section-header">
                <Form.Label className="form-label--title">{t('createWorker.address.title', 'Address Details')}</Form.Label>
            </div>
            <Form.Group controlId="address.street">
                <Form.Label className="form-label--smaller">
                    {t('common.address', 'Address')}
                </Form.Label>
                <Form.Control
                    name="address.street"
                    type="text"
                    placeholder={t('createWorker.address.examplePlaceholder', 'Unit 10, 23 Smith St')}
                    ref={register()}
                    isInvalid={!!errors?.address?.street}
                    size="lg"
                    disabled={disabled}
                    onClick={() => loadRecaptcha()}
                />
                {/* TODO: Re-enable this when its working again EPIC-454 */}
                {/*<AddressSearchControl*/}
                {/*    control={control}*/}
                {/*    name="address.address"*/}
                {/*    placeholder={t('createWorker.address.examplePlaceholder', 'Unit 10, 23 Smith St')}*/}
                {/*    onSelect={onAddressSelect}*/}
                {/*    size="lg"*/}
                {/*/>*/}
                {(errors && errors.address?.street) && (
                    <Form.Control.Feedback type="invalid">
                        {t('errors.address.address.missing', 'You must enter a valid street address')}
                    </Form.Control.Feedback>
                )}
            </Form.Group>
            <Form.Group controlId="address.town">
                <Form.Label className="form-label--smaller">
                    {t('common.suburbOrTown', 'Suburb or Town')}
                </Form.Label>
                <Form.Control
                    name="address.town"
                    type="text"
                    placeholder={t('common.suburbOrTown', 'Suburb or Town')}
                    ref={register()}
                    isInvalid={!!errors?.address?.town}
                    size="lg"
                    disabled={disabled}
                    onClick={() => loadRecaptcha()}
                />
                {(errors && errors.address?.town) && (
                    <Form.Control.Feedback type="invalid">
                        {t('errors.address.town.missing', 'You must enter a valid suburb or town')}
                    </Form.Control.Feedback>
                )}
            </Form.Group>
            <Form.Group controlId="address.country">
                <Form.Label className="form-label--smaller">
                    {t('common.country', 'Country')}
                </Form.Label>
                <CountryController
                    name="address.country"
                    control={control}
                    size="lg"
                    disabled={disabled}
                />
                {(errors && errors.address?.country) && (
                    <Form.Control.Feedback type="invalid">
                        {t('errors.address.country.missing', 'You must select a valid country')}
                    </Form.Control.Feedback>
                )}
            </Form.Group>
            <Row>
                <Col xs={7} sm={6}>
                    <Form.Group controlId="address.state">
                        <Form.Label className="form-label--smaller">
                            {t('common.state', 'State')}
                        </Form.Label>
                        <StateController
                            name="address.state"
                            control={control}
                            country={country}
                            size="lg"
                            disabled={disabled}
                        />
                        {(errors && errors.address?.state) && (
                            <Form.Control.Feedback type="invalid">
                                {t('errors.address.state.missing', 'You must select a valid state')}
                            </Form.Control.Feedback>
                        )}
                    </Form.Group>
                </Col>
                <Col xs={5} sm={6}>
                    <Form.Group controlId="address.postCode">
                        <Form.Label className="form-label--smaller">
                            {t('common.postcode', 'Postcode')}
                        </Form.Label>
                        <Form.Control
                            name="address.postCode"
                            type="text"
                            placeholder={t('common.postcode', 'Postcode')}
                            ref={register()}
                            isInvalid={!!errors?.address?.postCode}
                            size="lg"
                            disabled={disabled}
                            onClick={() => loadRecaptcha()}
                        />
                        {(errors && errors.address?.postCode) && (
                            <Form.Control.Feedback type="invalid">
                                {t('errors.address.postCode.missing', 'You must select a valid postcode')}
                            </Form.Control.Feedback>
                        )}
                    </Form.Group>
                </Col>
            </Row>
        </div>
    );
};
